import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/property-market/downtime/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import downtimeService from "data/downtime"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { handleReferences } from "data/lib/handle-references"
import { copy, paste, canPaste } from "data/lib/copy-paste"
// Styles
import { DowntimeTable_s } from "components/property-market/downtime/downtime-styles"

type ContextRow = {
  downtimeid: number
  propertyid: number
  code: string
  systeminput?: string
}

const DowntimeTable = (props: {tableId: string, data: object[]}) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => downtimeService.fetchDowntime(contextRow.propertyid)
      handleReferences('downtime', contextRow.downtimeid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: e => { if(contextRow.systeminput) return;
      downtimeService.copy(contextRow.propertyid, contextRow.downtimeid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const downtimes = await downtimeService.copies(contextRow.propertyid, contextRow.downtimeid, n)
      })
    }},
    {label: 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      const currentRowNum = getFocusedRowIndex() + 1
      showDeleteConfirmation(contextRow, currentRowNum)
    }},
    traceMenuItem
  ]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({downtimeid: 0, propertyid: 0, code: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('downtime')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const downtimeEventSubscription = downtimeService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "DOWNTIME_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "DOWNTIME_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            downtimeService.create(propertyService.getStoredPropertyId())
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[(pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1];
            if (!currentRow.systeminput) showDeleteConfirmation({downtimeid: currentRow.downtimeid, propertyid: currentRow.propertyid, code: currentRow.code}, currentRowNum)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      downtimeEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    const downtime = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view
    _.set(downtime, field, value)

    if (field === 'mosdowntime'){
      _.set(downtime, 'inputtype', 'basic');
      _.set(downtime, 'downtimedetail', null);
    }
    if (field === 'downtimedetail'){
      _.set(downtime, 'inputtype', 'detailed');
      _.set(downtime, 'mosdowntime', null);
    }
    if (value.state && value.state === 'none'){
      _.set(downtime, 'inputtype', 'none');
    }

    downtimeService.patch(downtime);
    if (value && downtime.inputtype) carouselService.updateRowData(downtime);
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, downtimeService.fetchDowntime, 'downtime')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([traceMenuItem]) : setMenu(nonSystemMenu)
  }, [contextCell])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    let pager = getPagingInfo(props.tableId)
    let row = currentRow + ((pager.currentPage - 1) * pager.pageSize);

    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        downtimeService.deleteOne(record.propertyid, record.downtimeid)
        setDeleteConfirmation(null)
        automationService.broadcast("MODAL_CLOSING", {})
        setTimeout(() => focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0), 200)
      },
      rowNum: row,
      code: record.code
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.downtimeid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  function updateContextMenu(originalEvent){
    setMenu(nonSystemMenu)
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    return {'' : rowData}
  }

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <DowntimeTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            header="Code"
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            header="Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            header="Input Type"
            {...field('downtimedetail', (rowData, props) =>
              <CarouselTrigger
                field="downtimedetail"
                rowData={rowData}
                title="Input Type"
                persist={persist}
                controls={{
                  basic: {control: Controls.InputTypeBasic, name: "Basic"},
                  detailed: {control: Controls.InputTypeDetailed, name: "Detailed"}
                }}
                basicReadonly={['mosdowntime']}
                hover={{
                  basic: [{key: 'mosdowntime', label: 'Downtime (Mos)', ignoreField: true}],
                  detailed: {multiple: [{key: ['downtimedetail', 'year'], label: 'Year'}, {key: ['downtimedetail', 'downtime'], label: 'Downtime (Mos)'}]}
                }}
              />,
              (err) => !_.includes(['code', 'name'], err.path[0])
            )}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={594} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </DowntimeTable_s>
    </div>
  )
}

export default DowntimeTable
